import { useEffect, useRef, useState } from 'react';
import { C } from '../theme';
import { Icon } from '../icons';
import { Spinner } from '../components/States';
import { useToast } from '../components/Toast';
import { api, ApiError } from '../api';
import type { MyTenancy, RequestCategory, RequestRow, RequestStatus } from '../api';
import { useAsync } from '../useAsync';

const CATEGORIES: Array<{ key: RequestCategory; label: string }> = [
  { key: 'plumbing', label: 'Plumbing' },
  { key: 'electrical', label: 'Electrical' },
  { key: 'ac', label: 'AC' },
  { key: 'structure', label: 'Structure' },
  { key: 'other', label: 'Other' },
];

// The 4-step pipeline shown for the latest request (SPEC request_event pipeline).
const PIPE: Array<{ label: string; reached: RequestStatus[] }> = [
  { label: 'Sent', reached: ['submitted', 'seen', 'assigned', 'in_progress', 'done'] },
  { label: 'Seen', reached: ['seen', 'assigned', 'in_progress', 'done'] },
  { label: 'Assigned', reached: ['assigned', 'in_progress', 'done'] },
  { label: 'Done', reached: ['done'] },
];

/** Tenant maintenance (GET/POST /my/requests): submit a request + track the latest. */
export function TenantRequests() {
  const notify = useToast();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [category, setCategory] = useState<RequestCategory>('plumbing');
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmedId, setConfirmedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const { data, loading, reload } = useAsync<RequestRow[]>(() => api.myRequests(), []);
  // Resolve the caller's tenancy id so a photo can be scoped to it (best-effort).
  const { data: tenancy } = useAsync<MyTenancy>(() => api.myTenancy(), []);
  const latest = data?.[0] ?? null;

  // Keep an object-URL preview in step with the chosen file; revoke on change/unmount.
  useEffect(() => {
    if (!photo) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  function pickPhoto(file: File | undefined) {
    if (file) setPhoto(file);
  }

  async function confirmFix(id: string) {
    setConfirming(true);
    try {
      await api.addRequestEvent(id, { note: 'Tenant confirmed the issue is fixed.' });
      setConfirmedId(id);
      notify('Thanks. Your owner has been notified.');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not confirm. Try again.');
    } finally {
      setConfirming(false);
    }
  }

  function clearPhoto() {
    setPhoto(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function submit() {
    const desc = description.trim();
    if (!desc) {
      notify('Add a short description first');
      return;
    }
    setBusy(true);
    try {
      // Best-effort: attach the photo as a tenancy-scoped document first, then
      // note it on the request so the owner can find it. A failed upload never
      // blocks the request itself.
      let attached = false;
      if (photo && tenancy?.tenancyId) {
        try {
          const fd = new FormData();
          fd.append('file', photo);
          fd.append('tenancyId', tenancy.tenancyId);
          fd.append('kind', 'other');
          fd.append('title', `Maintenance photo: ${cap(category)}`);
          await api.uploadDocument(fd);
          attached = true;
        } catch {
          // swallow — the request must still go through
        }
      }
      const description2 = attached ? `${desc}\n\n[Photo attached, see Documents]` : desc;
      const created = await api.createRequest({ category, description: description2 });
      notify(
        `Request submitted${created?.id ? ` · #${created.id.slice(0, 4)}` : ''}${attached ? ' · photo attached' : ''}`,
      );
      setDescription('');
      clearPhoto();
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not submit. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div>
        <label style={fieldLabel}>What needs attention?</label>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {CATEGORIES.map((o) => {
            const on = category === o.key;
            return (
              <button
                key={o.key}
                onClick={() => setCategory(o.key)}
                style={{ fontSize: 12.5, fontWeight: 600, padding: '7px 11px', borderRadius: 999, border: `1px solid ${on ? C.accent : C.line}`, color: on ? C.accent2 : C.ink2, background: on ? C.soft : C.card, cursor: 'pointer' }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label style={fieldLabel}>Describe it</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Water seepage on the back wall after the rains…"
          rows={3}
          style={{ width: '100%', resize: 'none', background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, padding: '11px 12px', fontSize: 13.5, color: C.ink, outline: 'none' }}
        />
      </div>

      <div>
        <label style={fieldLabel}>Add a photo</label>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => pickPhoto(e.target.files?.[0])}
        />
        {preview ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, border: `1px solid ${C.line}`, borderRadius: 11, padding: 8, background: C.card }}>
            <img
              src={preview}
              alt="Selected"
              style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, flex: 'none' }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 13, color: C.ink, fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {photo?.name}
              </b>
              <small style={{ fontSize: 12, color: C.muted }}>Attached to this request</small>
            </div>
            <button
              onClick={clearPhoto}
              aria-label="Remove photo"
              style={{ border: `1px solid ${C.line}`, background: C.card, color: C.ink2, fontSize: 12.5, fontWeight: 600, padding: '6px 11px', borderRadius: 9, cursor: 'pointer', flex: 'none' }}
            >
              Remove
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            style={{ width: '100%', height: 76, border: '1.5px dashed #cdc4bb', borderRadius: 11, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, color: C.muted, fontSize: 12.5, fontWeight: 600, background: C.card, cursor: 'pointer' }}
          >
            <Icon n="cam" size={18} color={C.muted} />
            Tap to attach a photo
          </button>
        )}
      </div>

      <button onClick={submit} disabled={busy} style={{ background: C.accent, color: '#fff', border: 0, borderRadius: 11, padding: 13, fontWeight: 650, fontSize: 14.5, width: '100%', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.8 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {busy && <Spinner size={15} color="#fff" />}
        {busy ? 'Submitting…' : 'Submit request'}
      </button>

      {/* latest request tracker */}
      {loading ? null : latest ? (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden', marginTop: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px 8px' }}>
            <h4 style={{ margin: 0, fontSize: 14, color: C.ink, fontWeight: 650 }}>
              Last request{latest.id ? ` · #${latest.id.slice(0, 4)}` : ''}
            </h4>
          </div>
          <div style={{ padding: '2px 14px 16px' }}>
            <div style={{ fontSize: 13, color: C.ink, fontWeight: 600, marginBottom: 12 }}>
              {latest.description || cap(latest.category)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {PIPE.map((step, i) => {
                const done = step.reached.includes(latest.status);
                return (
                  <div key={step.label} style={{ flex: 1, textAlign: 'center', position: 'relative', fontSize: 10.4, fontWeight: 600, color: done ? C.accent2 : C.muted }}>
                    {i > 0 && <div style={{ position: 'absolute', top: 8, left: '-50%', width: '100%', height: 2, background: done ? C.accent : '#ddd6cd', zIndex: 0 }} />}
                    <div style={{ position: 'relative', zIndex: 1, width: 16, height: 16, borderRadius: 999, background: done ? C.accent : '#ddd6cd', margin: '0 auto 5px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {done && <Icon n="check" size={10} color="#fff" />}
                    </div>
                    {step.label}
                  </div>
                );
              })}
            </div>

            {latest.status === 'done' && (
              <div style={{ marginTop: 14 }}>
                {confirmedId === latest.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.8, fontWeight: 600, color: C.hi, background: C.greenSoft, borderRadius: 10, padding: '10px 12px' }}>
                    <Icon n="check" size={15} /> You confirmed the fix
                  </div>
                ) : (
                  <button
                    onClick={() => confirmFix(latest.id)}
                    disabled={confirming}
                    style={{ width: '100%', background: C.card, color: C.accent2, border: `1.5px solid ${C.accent}`, borderRadius: 11, padding: 11, fontWeight: 650, fontSize: 13.5, cursor: confirming ? 'default' : 'pointer', opacity: confirming ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  >
                    {confirming ? <Spinner size={14} /> : <Icon n="check" size={15} />}
                    {confirming ? 'Confirming…' : 'Confirm it’s fixed'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 2, color: C.muted, fontSize: 12.5, textAlign: 'center', padding: '10px 0' }}>
          No requests yet. Your first one will show its progress here.
        </div>
      )}
    </>
  );
}

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const fieldLabel: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: C.ink2,
  display: 'block',
  marginBottom: 6,
};
